import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Facebook, Star } from "lucide-react";
import { ASSETS } from "../data/assets";
import { INSTAGRAM_PROFILE_URL, INSTAGRAM_HANDLE, FACEBOOK_URL, GOOGLE_REVIEW_URL } from "../data/videos";
import { legalConfig } from "../data/legalConfig";

export const Footer = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const link = (hash) => (isHome ? hash : `/${hash}`);

  return (
    <footer data-testid="site-footer" className="bg-[#0B1422] border-t border-[#C9A869]/20 py-14">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3 mb-5">
            <img src={ASSETS.logo} alt="Louis-Melchior Danse" className="h-12 w-auto" />
            <div className="leading-tight">
              <div className="serif text-xl text-[#F7F3EA]">Louis-Melchior</div>
              <div className="text-[10px] tracking-[0.35em] text-[#C9A869]">DANSE</div>
            </div>
          </div>
          <p className="text-sm text-[#F7F3EA]/60 leading-relaxed font-light">
            Cours de danse particuliers et collectifs.
          </p>
          <p className="mt-3 text-xs tracking-[0.25em] uppercase text-[#C9A869]/80">
            France & Luxembourg
          </p>

          <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
            <a
              href={INSTAGRAM_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#C9A869]/40 text-[#C9A869] hover:bg-[#C9A869] hover:text-[#0E1A2B] transition-colors duration-500"
            >
              <Instagram size={16} />
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="Facebook"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#C9A869]/40 text-[#C9A869] hover:bg-[#C9A869] hover:text-[#0E1A2B] transition-colors duration-500"
            >
              <Facebook size={16} />
            </a>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google-review"
              aria-label="Laisser un avis Google"
              title="Laisser un avis Google"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#C9A869]/40 text-[#C9A869] hover:bg-[#C9A869] hover:text-[#0E1A2B] transition-colors duration-500"
            >
              <Star size={16} />
            </a>
          </div>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A869] mb-4">Navigation</p>
          <ul className="space-y-2 text-sm text-[#F7F3EA]/75 font-light">
            <li><a href={link("#cours")} className="hover:text-[#C9A869] transition-colors">Cours</a></li>
            <li><a href={link("#mariage")} className="hover:text-[#C9A869] transition-colors">Mariage</a></li>
            <li><a href={link("#videos")} className="hover:text-[#C9A869] transition-colors">Vidéos</a></li>
            <li><a href={link("#avis")} className="hover:text-[#C9A869] transition-colors">Avis</a></li>
            <li><a href={link("#tarifs")} className="hover:text-[#C9A869] transition-colors">Tarifs</a></li>
          </ul>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A869] mb-4">Informations légales</p>
          <ul className="space-y-2 text-sm text-[#F7F3EA]/75 font-light">
            <li>
              <Link
                to="/mentions-legales"
                data-testid="footer-mentions-legales"
                className="hover:text-[#C9A869] transition-colors"
              >
                Mentions légales
              </Link>
            </li>
            <li>
              <Link
                to="/confidentialite"
                data-testid="footer-politique-confidentialite"
                className="hover:text-[#C9A869] transition-colors"
              >
                Politique de confidentialité
              </Link>
            </li>
            <li>
              <Link
                to="/conditions-generales"
                data-testid="footer-conditions-reservation"
                className="hover:text-[#C9A869] transition-colors"
              >
                Conditions générales
              </Link>
            </li>
            <li>
              <a
                href={link("#contact")}
                data-testid="footer-contact"
                className="hover:text-[#C9A869] transition-colors"
              >
                Contact
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A869] mb-4">Contact & réseaux</p>
          <ul className="space-y-2 text-sm text-[#F7F3EA]/75 font-light">
            <li>
              <a
                href={`mailto:${legalConfig.contactEmail}`}
                className="hover:text-[#C9A869] break-all"
              >
                {legalConfig.contactEmail}
              </a>
            </li>
            <li>
              <a
                href={INSTAGRAM_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#C9A869]"
              >
                Instagram {INSTAGRAM_HANDLE}
              </a>
            </li>
            <li>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#C9A869]"
              >
                Facebook
              </a>
            </li>
            <li>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#C9A869]"
              >
                Laisser un avis Google
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 mt-12 pt-6 border-t border-[#C9A869]/15 flex flex-col md:flex-row justify-between gap-3">
        <p className="text-xs text-[#F7F3EA]/50 tracking-wider">
          © {new Date().getFullYear()} {legalConfig.brandName}. Tous droits réservés.
        </p>
        <p className="text-xs text-[#F7F3EA]/40 tracking-wider serif italic">
          « Danser, c'est se connecter à l'autre, se dépasser et partager des moments inoubliables. »
        </p>
      </div>
    </footer>
  );
};
